import dynamic from 'next/dynamic';
import { ReactNode, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import TrailDetailsActionLocation from '@alltrails/amplitude/enums/TrailDetailsActionLocation';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import useUser from '@alltrails/context/hooks/useUser';
import type TrailDetails from '@alltrails/types/TrailDetails';
import Button from '@alltrails/denali/components/Button';
import IconButton from '@alltrails/denali/components/IconButton';
import IconButtonWithTooltip from '@alltrails/denali/components/IconButtonWithTooltip';
import Car from '@alltrails/denali/icons/Car';
import type { Size } from '@alltrails/denali/types';
import getTrailDirectionsUrl from '@/utils/getTrailDirectionsUrl';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

type DirectionsCtaProps = {
  buttonLocation?: TrailDetailsActionLocation;
  displayAsButton?: boolean;
  trailId: number;
  location: TrailDetails['location'];
  size?: Size<'sm' | 'md'>;
  text?: ReactNode;
  displayAsButtonWithTooltip?: boolean;
  isShortVersion?: boolean;
  isFullWidth?: boolean;
};

const DirectionsCta = ({
  buttonLocation,
  displayAsButton,
  trailId,
  location,
  size = 'md',
  displayAsButtonWithTooltip,
  isShortVersion,
  isFullWidth
}: DirectionsCtaProps) => {
  const intl = useIntl();

  const latitude = location?.latitude;
  const longitude = location?.longitude;
  const directionsUrl = getTrailDirectionsUrl(latitude, longitude);

  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const user = useUser();

  const onDirectionsClick = useCallback(
    e => {
      logTrailDetailsActionTapped({
        trail_id: trailId.toString(),
        trail_page_action: 'get directions',
        trail_page_location: buttonLocation
      });

      if (!user) {
        e.preventDefault();
        setIsSignupModalOpen(true);
        return;
      }
    },
    [buttonLocation, trailId, user]
  );

  const disabled = !latitude || !longitude;

  const renderCTA = () => {
    if (displayAsButton) {
      return (
        <Button
          disabled={disabled}
          {...(user ? { linkInfo: { href: directionsUrl, target: '_blank', rel: ['noopener', 'noreferrer'] } } : {})}
          onClick={onDirectionsClick}
          text={isShortVersion ? <FormattedMessage defaultMessage="Directions" /> : <FormattedMessage defaultMessage="Get directions" />}
          width={isFullWidth ? 'full' : undefined}
        />
      );
    } else if (displayAsButtonWithTooltip) {
      return (
        <IconButtonWithTooltip
          icon={{ Component: Car }}
          {...(user ? { linkInfo: { href: directionsUrl, target: '_blank', rel: ['noopener', 'noreferrer'] } } : {})}
          onClick={onDirectionsClick}
          title={intl.formatMessage({ defaultMessage: 'Directions' })}
          tooltipProps={{ tooltipPlacement: 'left', maxWidth: 200 }}
          size="md"
          variant="elevated"
        />
      );
    }
    return (
      <IconButton
        disabled={disabled}
        icon={{ Component: Car }}
        {...(user ? { linkInfo: { href: directionsUrl, target: '_blank', rel: ['noopener', 'noreferrer'] } } : {})}
        size={size}
        title={intl.formatMessage({ defaultMessage: 'Directions' })}
        onClick={onDirectionsClick}
      />
    );
  };

  return (
    <>
      {renderCTA()}
      {isSignupModalOpen && (
        <SignUpModal
          isOpen={isSignupModalOpen}
          closeModal={() => setIsSignupModalOpen(false)}
          deeplinkData={{ pageId: trailId, pageType: 'trail' }}
          onSuccess={() => {
            setIsSignupModalOpen(false);
            window.open(directionsUrl, '_blank', 'noopener noreferrer');
          }}
          trigger="Trail Detail Directions"
          upsellTrigger="Trail Detail Directions"
        />
      )}
    </>
  );
};

export default DirectionsCta;
